import { useEffect, useState } from "react";
import { Box, Button, Container, Grid, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { TabBar } from "../components/TabBar"
import { Attribute, MaxedValue, PlayerCharacter, PlayerCharacterDisplay, Skill, Ability, Trait } from "../model/character";
import { deleteCharacter, exportCharacter, loadCharacter, saveCharacter } from "../master/data";
import { SkillField } from "../components/SkillField";
import Bar from "../components/Bar";

import { AttributeField } from "../components/AttributeField";
import TraitDisplay from "./characterPage/TraitDisplay";
import SubStatsDisplay from "./characterPage/SubStatsDisplay";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { NumberField } from "../components/formFields/NumberField";
import Section from "../components/Section";
import WeaponDisplay from "./characterPage/WeaponDisplay";
import DamageDisplay from "./characterPage/DamageDisplay";
import AttributeDisplay from "../components/AttributeDisplay";
import OutfitDisplay from "./characterPage/OutfitDisplay";
import { CharacterContext } from "../model/characterContext";
import AbilityDisplay from "./characterPage/AbilityDisplay";
import ErrorBoundary from "../components/ErrorBoundary";
import EquipmentList from "./characterPage/equipment/EquipmentList";
import { ArmorAtZeroStat, ArmorStat, Defence, EnduranceCurrentStat, EnduranceDamage, EnduranceMaxStat, HitBonus, InfluenceMax, Resistance, ResourcesMax, UntrainedSkillModifier } from "../model/statsConstant";
import { ChangeTheme } from "../master/theme";
import SpeciesEditor from "../components/SpeciesSelector";
import CombatStatsView from "./characterPage/CombatStatsView";
import Page from "../components/Page";

import { ReactComponent as CharacterIcon } from "../components/icons/raw/Icon_Character.svg";
import { ReactComponent as MetaIcon } from "../components/icons/raw/Icon_Meta.svg";
import { ReactComponent as CritDamageIcon } from "../components/icons/raw/Icon_Crit_Damage.svg";
import { ReactComponent as RecordIcon } from "../components/icons/raw/Icon_Record.svg";
import { SpeciesList } from "../model/species";
import RollsDisplay from "./characterPage/RollsDisplay";
import ButtonFlat from "../components/ButtonFlat";
import ActionModal from "./characterPage/actionModal/ActionModal";
import { DeathModal } from "./characterPage/interactionModals/DeathModal";
import PostSessionModal from "./characterPage/postSessionModal/PostSessionModal";
import PostCombatModal from "./characterPage/postCombatModal/PostCombatModal";
import DetailPair from "./characterPage/DetailPair";
import CompanionWeaponEditorList from "./characterPage/companionsWeaponEditor";
import CompanionWeaponDisplay from "./characterPage/CompanionWeaponDisplay";

const displayBuilder = (character: PlayerCharacter, getAdjustment: (id: string) => number): PlayerCharacterDisplay => {
    const level = 1 + Math.floor(character.competence/character.competencePerLevel);

    const attributes = character.attributes.map((c) => {  
        let currentBonus = 0;
        let maxBonus = 0;
        let damage = 0;
        currentBonus += getAdjustment(c.id);
        maxBonus += getAdjustment(`${c.id}_max`);
        for(var dam of character.damages) {
            if(dam.targetId === c.id) {
                damage -= dam.adjustment;
            }
        }
        const max = c.max + maxBonus;
        const current = Math.min(c.alloted, max) + currentBonus;
        return {...c, max, current, withDamage: damage + current, damage };
    });

    const strongHits: Ability[] = [];

    let traitHitBonus = getAdjustment(HitBonus.id);
    let traitEndruanceDamage = getAdjustment(EnduranceDamage.id);
    const focusAttr = attributes.find(a => a.id === "foc");

    character.species.strongHits.forEach((ability) => strongHits.push(ability));

    const weapons = character.weapons.map((weapon) => {
        let critDamage = weapon.base.critDamage;
        let enduranceDamage = weapon.base.enduranceDamage + traitEndruanceDamage + (focusAttr?.current ?? 0);
        let hitDice = weapon.base.hitDice;
        let hitBonus = weapon.base.hitBonus + traitHitBonus;
        let range = weapon.base.range;
        let influence = weapon.base.influence;
        let knowledge = weapon.base.knowledge;
        let resources = weapon.base.resources;
        let slots = weapon.base.slots;
        let draw = weapon.base.draw;
        let hands = weapon.base.hands;
        let reload = weapon.base.reload;
        let strongHitRange = weapon.base.strongHitRange;
        const acquireStrings = [ weapon.base.acquire, ...weapon.variations.map((v) => v.acquire), ... weapon.modifications.map((m) => m.acquire)];
        const features = [ ...weapon.base.addFeatures ];
        strongHits.push(...weapon.base.strongHits);
        
        let skillBonus = 0 ;
        
        if(weapon.base.skillBonusId !== "")
        {
            const skill = character.combatSkills.find(a => a.id == weapon.base.skillBonusId);

            if(skill !== undefined){
                const bonusAttribute = attributes.find(a => a.id === skill.attributeId);
                const bonusAdustmentCalculator = (value: number) => {
                    if(value > 3) return 1;
                    if ( value < 2) return -1;
                    return 0;
                }
                const bonusAttributeAdjustment =  bonusAdustmentCalculator(bonusAttribute?.current ?? 2);
                const toolboxBonus = skill.toolbox ? 1 : 0
                let traitBonus = getAdjustment(skill.id);
                const untrainedModifier = getAdjustment(UntrainedSkillModifier.id);
                skillBonus = (skill.trained ? 1: (-2 + untrainedModifier)) + toolboxBonus + (skill.workshop ? 1 : 0) + bonusAttributeAdjustment + traitBonus;

            }
        } 

        for(const block of weapon.variations) {
            critDamage += block.critDamage;
            enduranceDamage += block.enduranceDamage;
            hitDice += block.hitDice;
            hitBonus += block.hitBonus;
            range += block.range;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            slots += block.slots;
            draw += block.draw;
            reload += block.reload;
            strongHitRange += block.strongHitRange;
            hands += block.hands;
            features.push( ...block.addFeatures);
            strongHits.push(...block.strongHits);
        }

        for(const block of weapon.modifications) {
            critDamage += block.critDamage;
            enduranceDamage += block.enduranceDamage;
            hitDice += block.hitDice;
            hitBonus += block.hitBonus;
            range += block.range;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            slots += block.slots;
            draw += block.draw;
            reload += block.reload;
            strongHitRange += block.strongHitRange;
            hands += block.hands;
            features.push(...block.addFeatures);
            strongHits.push(...block.strongHits);
        }

        return {
            id: weapon.id,
            name: weapon.name,
            description: weapon.description,
            isStashed: weapon.isStashed,

            acquire: acquireStrings.join(", "),
            critDamage,
            enduranceDamage,
            hitDice,
            hitBonus,
            skillBonus,
            range,
            influence,
            resources,
            knowledge,
            slots,
            draw,
            features,
            reload,
            strongHitRange,
            hands,
            carried: weapon.carried,
            weaponType: weapon.base.weaponType,
            munitionsSpent: weapon.munitionsSpent,
            reloadsPerformed: weapon.reloadsPerformed,
            variations: weapon.variations.map(v => v.name),
            modifications: weapon.modifications.map(m => m.name)
        }
    })

    const companionWeapons = character.companionWeapons.map((weapon) => {
        let critDamage = weapon.base.critDamage;
        let enduranceDamage = weapon.base.enduranceDamage + traitEndruanceDamage;
        let hitDice = weapon.base.hitDice;
        let hitBonus = weapon.base.hitBonus + traitHitBonus;
        let range = weapon.base.range;
        let influence = weapon.base.influence;
        let knowledge = weapon.base.knowledge;
        let resources = weapon.base.resources;
        let slots = weapon.base.slots;
        let armour = weapon.base.armour;
        let defence = weapon.base.defence;
        let mobility = weapon.base.mobility;
        let bodies = weapon.base.bodies;
        let durability = weapon.base.durability;
        let reload = weapon.base.reload;
        let strongHitRange = weapon.base.strongHitRange;
        const acquireStrings = [ weapon.base.acquire, ...weapon.variations.map((v) => v.acquire), ... weapon.modifications.map((m) => m.acquire)];
        const features = [ ...weapon.base.addFeatures ];
        strongHits.push(...weapon.base.strongHits);
        
        let skillBonus = 0 ;
        
        if(weapon.base.skillBonusId !== "")
        {
            const skill = character.combatSkills.find(a => a.id == weapon.base.skillBonusId);

            if(skill !== undefined){
                const bonusAttribute = attributes.find(a => a.id === skill.attributeId);
                const bonusAdustmentCalculator = (value: number) => {
                    if(value > 3) return 1;
                    if ( value < 2) return -1;
                    return 0;
                }
                const bonusAttributeAdjustment =  bonusAdustmentCalculator(bonusAttribute?.current ?? 2);
                const toolboxBonus = skill.toolbox ? 1 : 0
                let traitBonus = getAdjustment(skill.id);
                const untrainedModifier = getAdjustment(UntrainedSkillModifier.id);
                skillBonus = (skill.trained ? 1: (-2 + untrainedModifier)) + toolboxBonus + (skill.workshop ? 1 : 0) + bonusAttributeAdjustment + traitBonus;

            }
        } 

        for(const block of weapon.variations) {
            critDamage += block.critDamage;
            enduranceDamage += block.enduranceDamage;
            hitDice += block.hitDice;
            hitBonus += block.hitBonus;
            range += block.range;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            slots += block.slots;
            reload += block.reload;
            strongHitRange += block.strongHitRange;
            armour += block.armour;
            defence += block.defence;
            mobility += block.mobility;
            bodies += block.bodies;
            durability += block.durability;
            features.push( ...block.addFeatures);
            strongHits.push(...block.strongHits);
        }

        for(const block of weapon.modifications) {
            critDamage += block.critDamage;
            enduranceDamage += block.enduranceDamage;
            hitDice += block.hitDice;
            hitBonus += block.hitBonus;
            range += block.range;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            slots += block.slots;
            reload += block.reload;
            strongHitRange += block.strongHitRange;
            armour += block.armour;
            defence += block.defence;
            mobility += block.mobility;
            bodies += block.bodies;
            durability += block.durability;
            features.push(...block.addFeatures);
            strongHits.push(...block.strongHits);
        }

        return {
            id: weapon.id,
            name: weapon.name,
            description: weapon.description,
            isStashed: weapon.isStashed,

            acquire: acquireStrings.join(", "),
            critDamage,
            enduranceDamage,
            hitDice,
            hitBonus,
            skillBonus,
            range,
            influence,
            resources,
            knowledge,
            slots,
            features,
            reload,
            strongHitRange,
            armour,
            mobility,
            defence,
            durability,
            bodies,
            weaponType: weapon.base.weaponType,
            munitionsSpent: weapon.munitionsSpent,
            reloadsPerformed: weapon.reloadsPerformed,
            variations: weapon.variations.map(v => v.name),
            modifications: weapon.modifications.map(m => m.name)
        }
    })

    const outfits = character.outfits.map((outfit) => {
        let armor = outfit.base.armor;
        let armorAtZero = outfit.base.armorAtZero;
        let endurance = outfit.base.endurance;
        let defence = outfit.base.defence;
        let slots = outfit.base.slots;
        let influence = outfit.base.influence;
        let knowledge = outfit.base.knowledge;
        let resources = outfit.base.resources;
        const acquireStrings = [ outfit.base.acquire, ...outfit.variations.map((v) => v.acquire), ... outfit.modifications.map((m) => m.acquire)];
        const features = [ ...outfit.base.addFeatures ];

        for(const block of outfit.variations) {
            armor += block.armor;
            armorAtZero += block.armorAtZero;
            endurance += block.endurance;
            defence += block.defence;
            slots += block.slots;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            features.push(...block.addFeatures);
        }

        for(const block of outfit.modifications) {
            armor += block.armor;
            armorAtZero += block.armorAtZero;
            endurance += block.endurance;
            defence += block.defence;
            slots += block.slots;
            influence += block.influence;
            knowledge += block.knowledge;
            resources += block.resources;
            features.push(...block.addFeatures);
        }

        return {
            id: outfit.id,
            name: outfit.name,
            description: outfit.description,
            isWorn: outfit.carried === "worn",
            isStashed: outfit.isStashed,

            acquire: acquireStrings.join(", "),
            armor,
            armorAtZero,
            defence,
            endurance,
            slots,
            influence,
            knowledge,
            resources,
            outfitType: outfit.base.outfitType,
            features,
        }
    })

    for(const utility of character.utilities) {
        strongHits.push(...utility.strongHits);
    }

    for(const equipment of character.equipment) {
        strongHits.push(...equipment.strongHits);
    }

    for(const trait of character.traits) {
        strongHits.push(...trait.strongHits);
    }

    for(const trait of character.perks) {
        strongHits.push(...trait.strongHits);
    }

    for(const trait of character.complications) {
        strongHits.push(...trait.strongHits);
    }

    let equipmentCurrent = 0
    for(const weapon of weapons) {
        if(!(weapon.carried === "held") && !weapon.isStashed)
            equipmentCurrent += weapon.slots;
    }
    for(const outfit of outfits) {
        if(!outfit.isWorn && !outfit.isStashed)
            equipmentCurrent += 6 + outfit.slots;

    }
    for(const equipment of character.equipment) {
        if(!equipment.isStashed) 
            equipmentCurrent += equipment.slots;
    }
    for(const utility of character.utilities) {
        if(!utility.isStashed) 
            equipmentCurrent += utility.slots;
    }
    const equipmentMax = 6 + (character.attributes.find(a => a.id == "str")?.alloted ?? 0);

    return {
        name: character.name,
        competence: character.competence,
        competencePerLevel: character.competencePerLevel,
        level: level,
        inventory: { ...character.inventory },
        attributes,
        combatSkills: [...character.combatSkills],
        primarySkills: [...character.primarySkills],
        spaceSkills: [...character.spaceSkills],
        traits: [...character.traits],
        complications: character.complications,
        complicationsMin: 1 + Math.floor(level/5),
        perks: character.perks,
        perksMin: Math.floor(character.inventory.currentInfluence/5),
        equipmentSlots: equipmentMax,
        equipmentSlotsCurrent: equipmentCurrent,
        equipment: character.equipment,
        outfits,
        utility: character.utilities,
        weapons,
        companionWeapons,
        weaponSlots: 3,
        tradeGoods: [],
        selectedWeapon: "",
        damages: character.damages,
        strongHits,
    }
}

const makeAdjustments = (character: PlayerCharacter) => {
    const adjustments: {[k: string]: number} = {};

    const addAdjustment = (id: string, value: number) => {
        if(adjustments[id] === undefined){
            adjustments[id] = value;
        } else {
            adjustments[id] += value;
        }
    }

    for(var trait of character.traits) {
        for(var adjustment of trait.adjustments){
            addAdjustment(adjustment.targetId, adjustment.adjustment);
        }
    }

    for(var trait of character.perks) {
        for(var adjustment of trait.adjustments){
            addAdjustment(adjustment.targetId, adjustment.adjustment);
        }
    }

    for(var trait of character.complications) {
        for(var adjustment of trait.adjustments){
            addAdjustment(adjustment.targetId, adjustment.adjustment);
        }
    }

    for(var adjustment of character.species.adjustments){
        addAdjustment(adjustment.targetId, adjustment.adjustment);
    }

    for(var outfit of character.outfits) {
        if(outfit.carried === "worn") {
            let end = outfit.base.endurance;
            for(var mod of outfit.modifications){
                end += mod.endurance;
            }
            for(var variation of outfit.variations){
                end += variation.endurance;
            }
            addAdjustment(EnduranceMaxStat.id, end);
        }
    }

    const int = character.attributes.find(a => a.id === "int")?.alloted ?? 0;
    switch(character.cover) {
        case "Light":
            addAdjustment(Defence.id, int);
            break;
        case "Heavy":
            addAdjustment(Defence.id, int*2);
            addAdjustment(Resistance.id, 2);
            break;
        case "Entrenched":
            addAdjustment(Defence.id, int*3);
            addAdjustment(Resistance.id, 2);
            addAdjustment(ArmorStat.id, 1);
            addAdjustment(ArmorAtZeroStat.id, 1);
            break;
    }

    return adjustments;
}

const DeleteCharacterButton = (props: { character: PlayerCharacter}) => {
    const navigate = useNavigate();
    const [ showConfirm, setShowConfirm ] = useState(false);
    
    const handleYes = () => {
        setShowConfirm(false);
        deleteCharacter(props.character.id);
        navigate("/");
    }
    
    const handleNo = () => {
        setShowConfirm(false)
    }
    
    return <>
        <ConfirmDialog show={showConfirm} message={`Are you sure you want to delete ${props.character.name}`} onNo={handleNo} onYes={handleYes} />
        <Button onClick={() => setShowConfirm(true)}>Delete</Button>
    </>
}




export const CharacterPage = () => {
    const { characterKey } = useParams();
    const [ character, setCharacter ] = useState<PlayerCharacter>(loadCharacter(characterKey ?? ""));
    const [ adjustments, setAdjustments] = useState(makeAdjustments(character));
    const [ showActions, setShowActions ] = useState(false);
    const [ showDead, setShowDead ] = useState(false);
    const [ showPostCombat, setShowPostCombat ] = useState(false);
    const [ showPostSession, setShowPostSession ] = useState(false);

    const SetCharacter = (character: PlayerCharacter) => {
        const getEndurance = (char: PlayerCharacter) => {
            const adjustments = makeAdjustments(char);

            const enduranceDamage = char.damages.find(d => d.targetId === "endurance")?.adjustment ?? 0;
            const enduranceMaxDamage = char.damages.find(d => d.targetId === "enduranceMax")?.adjustment ?? 0;
            const strengthDamage = char.damages.find(d => d.targetId === "str")?.adjustment ?? 0;

            const strength = (char.attributes.find(a => a.id == "str")?.alloted ?? 0) + adjustments["str"] - strengthDamage;

            const enduranceMax = 10 + (Math.max(strength ?? 0, 0)) * 5 + (adjustments[EnduranceMaxStat.id] ?? 0) - enduranceMaxDamage;
            return enduranceMax - enduranceDamage;
        }

        const getAttribute = (char: PlayerCharacter, attr: Attribute) => {
            const adjustments = makeAdjustments(char);
            const value = char.attributes.find(a => a.id == attr.id)?.alloted ?? 0;
            const damage = char.damages.find(d => d.targetId === attr.id)?.adjustment ?? 0;

            return value + (adjustments[attr.id] ?? 0) - damage;
        }

        setCharacter((current) => {
            for(const attr of current.attributes) {
                const currentAttr = getAttribute(current, attr);
                const nextAttr = getAttribute(character, attr);

                if(nextAttr <= -5 && currentAttr > -5){
                    setShowDead(true);
                    return character;
                }
            }

            const currentEndurance = getEndurance(current);
            const characterEndurance = getEndurance(character);

            if(characterEndurance === 0 && currentEndurance !== 0) {
                setShowDead(true);
                return character;
            }
            return character;
        })
    }


    const GetAdjustment = (id: string) => {
        return adjustments[id] ?? 0;
    }
    
    const [ display, setDisplay ] = useState<PlayerCharacterDisplay>(displayBuilder(character, GetAdjustment));
    
    const GetAttribute = (id: string) => {
        let attr = display.attributes.find(a => a.id === id);
        if(attr === undefined){
            attr = {
                alloted: 0,
                current: 0,
                damage: 0,
                id,
                max: 0,
                name: "Invalid",
                withDamage: 0
            }
        }
        return attr;
    }

    useEffect(() => {
        saveCharacter(character);
        setAdjustments(makeAdjustments(character));
        setDisplay(displayBuilder(character, GetAdjustment));

        const color = character.species.color !== "" ? character.species.color : "#3c61b9";
        ChangeTheme( color, color);
    }, [character]);

    if(characterKey === undefined || character === undefined) {
        return <>No Character Key</>
    }

    const SetNotes = (value: string) => {
        SetCharacter({
            ...character,
            notes: value
        })
    }

    const SetCombatSkill = (value: Skill) => {
        SetCharacter({
            ...character,
            combatSkills: character.combatSkills.map((s) => {
                return s.id === value.id ? value : s;
            })
        })
    }

    const SetSpaceSkill = (value: Skill) => {
        SetCharacter({
            ...character,
            spaceSkills: character.spaceSkills.map((s) => {
                return s.id === value.id ? value : s;
            })
        })
    }

    const SetSkill = (value: Skill) => {
        SetCharacter({
            ...character,
            primarySkills: character.primarySkills.map((s) => {
                return s.id === value.id ? value : s;
            })
        })
    }

    const SetAttribute = (value: Attribute) => {
        SetCharacter({
            ...character,
            attributes: character.attributes.map((a) => {
                return a.id === value.id ? value : a;
            })
        })
    }

    const SetValue = (name: string, value: any) => {
      SetCharacter({
        ...character,
        [name]: value
      })
    }

    const SetInventoryValues = (values: { [k:string]: number}) => {
        SetCharacter({
            ...character,
            inventory: {
                ...character.inventory,
                ...values
            }
        })
    }

    const setLimitedUse = (key: string, max: number) => {
        const limitedUse: MaxedValue = { current: max, max};
        const limitedUses = { ...character.limitedUses };
        limitedUses[key] = limitedUse

        SetCharacter({
                ...character,
                limitedUses
            })
    }

    const removeLimitedUse = (key: string) => {
        const limitedUses = { ...character.limitedUses };
        delete limitedUses[key];
        SetCharacter({
                ...character,
                limitedUses
            })
    }

    const ContextButtons = () => {
        return <Grid container spacing={1} marginTop={1}>
            <Grid item xs={4}>
                <ButtonFlat fullWidth={true} onClick={() => setShowActions(true)}>Actions</ButtonFlat>
            </Grid>
            <Grid item xs={4}>
                <ButtonFlat fullWidth={true}  onClick={() => setShowPostCombat(true)}>Post Combat</ButtonFlat>
            </Grid>
            <Grid item xs={4}>
                <ButtonFlat fullWidth={true}  onClick={() => setShowPostSession(true)}>Post Session</ButtonFlat>
            </Grid>
        </Grid>
    }


    const strongHits = display.strongHits.filter(sh => sh.type === "Strong Hit");
    const abilities = display.strongHits.filter(sh => sh.type !== "Strong Hit");

    const playPanel = <Stack spacing={2}>
        <ContextButtons />
        <Section title="Attributes" collapsable>
            <Grid container spacing={1} marginBottom={2}>
            {display.attributes.map((attr) => {
                return <AttributeDisplay value={attr} />
            })}
            </Grid>
            <CombatStatsView character={character} characterDisplay={display} setDamage={(value) => SetValue("enduranceDamageTaken", value)} setCover={(value) => SetValue('cover', value)} />
            <SubStatsDisplay character={display} />
        </Section>
        <DamageDisplay character={character} setCharacter={SetCharacter} />
        {display.weapons.length > 0 && <Section title="Weapons" collapsable>
            <WeaponDisplay characterDisplay={display} character={character} setCharacter={setCharacter} />
        </Section>}
        {display.companionWeapons.length > 0 && <Section title="Companions (Weapons)" collapsable>
            <CompanionWeaponDisplay characterDisplay={display} character={character} setCharacter={setCharacter} />
        </Section>}
        {display.outfits.length > 0 && <Section title="Outfits" collapsable>
            <OutfitDisplay characterDisplay={display} setSelected={() => undefined} />
        </Section>}
        {strongHits.length > 0 && <Section title="Strong Hits" collapsable>
            <AbilityDisplay isStrongHit abilities={strongHits} />
        </Section>}
        {abilities.length > 0 && <Section title="Abilities" collapsable>
            <AbilityDisplay abilities={abilities} />
        </Section>}
        <Section title="Notes" collapsable>
            <TextField value={character.notes} onChange={(event) => SetNotes(event.currentTarget.value)} multiline fullWidth />
        </Section>
    </Stack>

    const influenceMax = display.level + 3 + GetAdjustment(InfluenceMax.id);
    const resourceMax = display.level + 3 + GetAdjustment(ResourcesMax.id);
    const inventoryPanel = <>
            <Section title="Progression">
                <Box>
                    <DetailPair
                        convertCost={4}
                        leftValue={character.competence}
                        leftLabel="Game Sessions"
                        leftCustomDisplay={(v) => `${v % character.competencePerLevel}/${character.competencePerLevel}`}
                        leftValues={[]}
                        rightValue={display.level}
                        rightLabel="Level"
                        onUpdate={(l, r, lu, ru) => {
                            let comp = l;
                            if(ru) {
                                const difference = r - display.level;
                                comp = l + (difference * character.competencePerLevel);
                            }
                            SetCharacter({
                                    ...character,
                                    competence: comp
                                })
                        }}
                        hideConvert
                        />
                <Typography fontWeight={700} paddingLeft={1}>Resources</Typography>
                    <DetailPair
                        convertCost={4}
                        leftValue={display.inventory.money}
                        leftLabel="Money"
                        leftValues={[]}
                        rightValue={display.inventory.currentResources}
                        rightMax={resourceMax}
                        rightLabel="Resources"
                        onUpdate={(l, r) => {
                            SetInventoryValues({
                                money: l,
                                currentResources: r
                            })
                        }}
                        />
                    <DetailPair
                        convertCost={4}
                        leftValue={display.inventory.loot}
                        leftLabel="Loot"
                        leftValues={[]}
                        rightValue={display.inventory.currentResources}
                        rightMax={resourceMax}
                        rightLabel="Resources"
                        onUpdate={(l, r) => {
                            SetInventoryValues({
                                loot: l,
                                currentResources: r
                            })
                        }}
                        hideRight
                        />
                    <DetailPair
                        convertCost={4}
                        leftValue={display.inventory.freight}
                        leftLabel="Freight"
                        leftValues={[]}
                        rightValue={display.inventory.currentResources}
                        rightMax={resourceMax}
                        rightLabel="Resources"
                        onUpdate={(l, r) => {
                            SetInventoryValues({
                                freight: l,
                                currentResources: r
                            })
                        }}
                        hideRight
                        />
                </Box>
                <Box marginTop={2}>
                    <Typography fontWeight={700} paddingLeft={1}>Research</Typography>
                    <DetailPair
                        convertCost={4}
                        leftValue={display.inventory.researchUnits}
                        leftLabel="Research Units"
                        leftValues={[]}
                        rightValue={display.inventory.currentKnowledge}
                        rightLabel="Knowledge"
                        onUpdate={(l, r) => {
                            SetInventoryValues({
                                researchUnits: l,
                                currentKnowledge: r
                            })
                        }}
                        />
                </Box>
                <Box marginTop={2}>
                    <Typography fontWeight={700} paddingLeft={1}>Networking</Typography>
                    <DetailPair
                        convertCost={4}
                        leftValue={display.inventory.networkUnits}
                        leftLabel="Networking Units"
                        leftValues={[]}
                        rightValue={display.inventory.currentInfluence}
                        rightMax={influenceMax}
                        rightLabel="Influence"
                        onUpdate={(l, r) => {
                            SetInventoryValues({
                                networkUnits: l,
                                currentInfluence: r
                            })
                        }}
                        />
                </Box>
            </Section>
            <CompanionWeaponEditorList setCharacter={SetCharacter} />
            <EquipmentList character={character} characterDisplay={display} setCharacter={SetCharacter} />
    </>;
    
    const statsPanel = <Stack spacing={2}>
        <ContextButtons />
        <Section title="Skills" collapsable>
            <Typography fontWeight={700} marginBottom={1} paddingLeft={1}>Primary</Typography>
            <Grid container>
            {display.primarySkills.map((a, index) => {
                return <SkillField
                key={a.id}
                    showExtended={true}
                    onChange={(skill) => SetSkill(skill)}
                    value={a}
                    last={index === display.primarySkills.length-1}
                    character={display}
                    toolboxEnabled
                    />
                })}
            </Grid>
            <Typography fontWeight={700} marginBottom={1} paddingLeft={1}>Personal Combat</Typography>
            <Grid container>
            {display.combatSkills.map((a, index) => {
                return <SkillField
                    key={a.id}
                    toolboxEnabled={false}
                    showExtended={true}
                    onChange={(skill) => SetCombatSkill(skill)}
                    value={a}
                    last={index === display.combatSkills.length-1}
                    character={display}
                    />
            })}
            </Grid>
            <Typography fontWeight={700} marginBottom={1} paddingLeft={1}>Spaceship Combat</Typography>
            <Grid container>
            {display.spaceSkills.map((a, index) => {
                return <SkillField
                key={a.id}
                toolboxEnabled={false}
                showExtended={true}
                onChange={(skill) => SetSpaceSkill(skill)}
                value={a}
                last={index === display.spaceSkills.length-1}
                character={display}
                />
            })}
            </Grid>
            <RollsDisplay character={display} />
        </Section>
        <Section title="Attributes" collapsable>
            <Grid container>
                {character.attributes.map((a, index) => 
                    <AttributeField character={character} value={a} key={a.id} last={index === display.attributes.length-1} onChange={(attribute) => SetAttribute(attribute)} />)}
            </Grid>
        </Section>
        <TraitDisplay libraryPrefix="ltr" title="Species and Traits" readonlyTraits={[character.species]} traits={character.traits} character={character} onChange={(traits) => SetValue("traits", traits)} />
        <TraitDisplay libraryPrefix="lco" title="Complications" traits={character.complications} character={character} onChange={(traits) => SetValue("complications", traits)} />
        <TraitDisplay libraryPrefix="lpe" title="Perks" traits={character.perks} character={character} onChange={(traits) => SetValue("perks", traits)} />
        <Section title="Notes" collapsable>
            <TextField value={character.notes} onChange={(event) => SetNotes(event.currentTarget.value)} multiline fullWidth />
        </Section>
    </Stack>
    
    const detailsPanel = <Stack spacing={1}>
        <Section title="Character Details">
          <Stack direction={"column"} spacing={2}>
            <Box>
                <Typography>Name</Typography>
                <TextField value={character.name} onChange={(value) => SetValue("name", value.currentTarget.value)} fullWidth />
            </Box>
            <Box>
                <Typography>Backstory</Typography>
                <TextField value={character.backstory} onChange={(value) => SetValue("backstory", value.currentTarget.value)} multiline fullWidth />
            </Box>
            <Box>
                <Typography>Species</Typography>
                <SpeciesEditor character={character} onSpeciesChange={(value) => {
                    SetValue("species", value); console.dir(value)}} />
                <Select
                    value={character.subculture}
                    onChange={(e) => SetValue("subculture", e.target.value)}>
                    {SpeciesList[character.species.id]?.subcultures.map((sc) => <MenuItem value={sc}>{sc}</MenuItem>)}
                </Select>
            </Box>
          </Stack>
        </Section>
        <Section title={`Level ${display.level}`}>
          <Grid container>
              <Grid item xs={6}>
                  <Bar label='Game Sessions'>
                      <NumberField step={1} onChange={(value) => SetValue("competence", value)} value={display.competence} min={0} />
                  </Bar>
              </Grid>
              <Grid item xs={6}>
                  <Bar label='+1 level per Sessions'>
                      <NumberField step={1} onChange={(value) => SetValue('competencePerLevel', value)} value={display.competencePerLevel} min={1} />
                  </Bar>
              </Grid>
          </Grid>
        </Section>
        <Bar label="Export Character">
            <Button onClick={() => exportCharacter(character)}>Export</Button>
        </Bar>
        <Bar label="Manage Character">
            <DeleteCharacterButton character={character} />
        </Bar>
    </Stack>

    return <>
        <ErrorBoundary fallback={(error) => <>
            <Page>
                <Typography paddingY={3} variant="h4">There was an error displaying this character.</Typography>
                <Typography >{error.componentStack}</Typography>
                </Page></>}>
            <CharacterContext.Provider value={{
                removeLimitedUse,
                setLimitedUse,
                character,
                characterDisplay: display,
                getAdjustment: GetAdjustment,
                getAttribute: GetAttribute,
                }}>
                <Page>
                    <Stack spacing={1} direction={"row"}>
                        <Box flexGrow={1} display={"flex"} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
                            <Typography variant="h3" fontWeight={700} fontSize={28} textAlign="center" style={{ overflowWrap: "anywhere"}}>{character.name}</Typography>
                            <Typography fontSize={15} textAlign="center" style={{ overflowWrap: "break-word"}}>Lvl {display.level}, {character.species.name} {character.subculture}</Typography>
                        </Box>
                        <img src={character.species.image} style={{ width:"90px", aspectRatio:"1/1", flexShrink: 0}} />
                    </Stack>
                <TabBar pages={[
                    { label: <><CharacterIcon height={"2em"} /></>, content: statsPanel },
                    { label: <><CritDamageIcon height={"2em"} /></>, content: playPanel},
                    { label: <><RecordIcon height={"2em"} /></>, content: inventoryPanel },
                    { label: <><MetaIcon height={"2em"} /></>, content: detailsPanel}
                ]} />
                </Page>
                <PostSessionModal onClose={() => setShowPostSession(false)} open={showPostSession} setCharacter={SetCharacter} />
                <PostCombatModal onClose={() => setShowPostCombat(false)} open={showPostCombat} setCharacter={SetCharacter} />
                <ActionModal onClose={() => setShowActions(false)} open={showActions} setCharacter={SetCharacter} />
                <DeathModal open={showDead} onClose={() => setShowDead(false)} />
            </CharacterContext.Provider>
        </ErrorBoundary>
    </>
}